import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import React, {useMemo, useRef} from 'react';

import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {useViewAction} from '../../state/views/hooks';
import {pinSection} from '../../state/views/panelBankConfig/actions';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {PanelBankSectionHeader} from './PanelBankSectionHeader2';
import {SectionWithRef} from './types';
import {
  getIsHiddenPanelsSection,
  useIsSectionVisible,
} from './useIsSectionVisible';
import {useSectionNameMatch} from './useSectionNameMatch';
import {useSectionPanels} from './useSectionPanels';
import {getDndSortableId} from './utils';

export const SortablePanelBankSection = ({
  idx,
  sectionWithRef,
  numSections,
}: {
  idx: number;
  sectionWithRef: SectionWithRef;
  numSections: number;
}) => {
  const sectionComponentRef = useRef<HTMLDivElement>(null);

  const sectionRef = sectionWithRef.ref;
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const pinPanelBankSection = useViewAction(panelBankConfigRef, pinSection);

  const {debouncedSearchRegex: searchRegex} = usePanelSearchDebouncedContext();
  const isSectionNameSearchMatch = useSectionNameMatch(
    sectionWithRef.name,
    searchRegex
  );

  const {activePanels, filteredPanels} = useSectionPanels(
    sectionWithRef,
    searchRegex
  );

  const isHiddenPanelsSection = useMemo(() => {
    return getIsHiddenPanelsSection({
      idx,
      numSections,
      sectionName: sectionWithRef.name,
    });
  }, [idx, numSections, sectionWithRef.name]);

  const isSectionVisible = useIsSectionVisible({
    activePanelsCount: activePanels.length,
    filteredPanelsCount: filteredPanels.length,
    isHiddenPanelsSection,
    isSectionNameSearchMatch,
    sectionWithRef,
  });

  const {setNodeRef, transform, transition} = useSortable({
    id: getDndSortableId(sectionWithRef.ref),
    data: {
      // This helps indicate if we're dragging a section or panel
      type: 'section',
    },
  });

  if (!isSectionVisible) {
    return null;
  }

  return (
    <SectionSettingsContextProvider sectionRef={sectionRef}>
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}>
        <PanelBankSectionHeader
          activePanelCount={activePanels.length}
          isPinned={sectionWithRef.pinned}
          isSectionNameSearchMatch={isSectionNameSearchMatch}
          panelBankSectionConfigRef={sectionRef}
          isHiddenPanelsSection={isHiddenPanelsSection}
          panelSearchMatchCount={filteredPanels.length}
          pinPanelBankSection={pinPanelBankSection}
          sectionComponentRef={sectionComponentRef}
          sectionLayoutType={sectionWithRef.type}
          sectionName={sectionWithRef.name}
        />
      </div>
    </SectionSettingsContextProvider>
  );
};
